<template>
    <div>

        <claims-dashboard-icons></claims-dashboard-icons>

        <div class="o-date-stepped-inputs__section-content u-d-flex a-form-inputs">
            <div class="o-date-stepped-inputs__section-content-header">
                <h2>Period {{ index +1 }} ({{ period.startOfPeriod }} - {{ period.endOfPeriod }})</h2>
                <div class="u-d-flex">
                    <div @click="toggleFirst">
                        <icon-tooltip
                                tooltip-name="period-toggle"
                                tooltip-text="To toggle the period between minimized and maximized just select this icon."
                                side="left"
                                text-width="200"
                                icon-type="toggle"
                                icon-colour="m-icon--dark">
                        </icon-tooltip>
                    </div>
                    <div @click="removePeriod()">
                        <icon-tooltip
                                tooltip-name="period-delete"
                                tooltip-text="To delete the period just select this icon. This will delete all data held within the period."
                                side="left"
                                text-width="200"
                                icon-type="delete"
                                icon-colour="m-icon--dark">
                        </icon-tooltip>
                    </div>
                </div>
            </div>

            <div class="u-d-flex u-flex-wrap a-form-inputs u-full-width">
                <div class="flex-grid__item--third">
                    <div class="u-margin-bottom-20 u-bold">
                        From
                    </div>
                    <control for="start_of_period" :table-control="true" :icon-space="false">
                        <date-input
                                v-model="period.startOfPeriod"
                                :disabled="index === 0"
                                v-field-control:start_of_period.required="{type:'date'}"
                        ></date-input>
                        <control-error :show="$error('start_of_period')">
                            The start of period date must be a valid date (DD/MM/YYYY).
                        </control-error>
                    </control>
                </div>
                <div class="flex-grid__item--third">
                    <div class="u-margin-bottom-20 u-bold">
                        To
                    </div>
                    <control for="end_of_period" :table-control="true" :icon-space="false">
                        <date-input
                                v-model="period.endOfPeriod"
                                @input="updatePostInjuryIncomePeriod()"
                                v-field-control:end_of_period.required="{type:'date'}"
                        ></date-input>
                        <control-error :show="$error('end_of_period')">
                            The end of period date must be a valid date (DD/MM/YYYY).
                        </control-error>
                        <control-error :show="afterEndOfPeriodRule">
                            The end of period date must be before the date of assumed settlement ({{ getClaimInfo('dateOfAssumedSettlement') }}).
                        </control-error>
                    </control>
                </div>
                <div class="flex-grid__item--third" v-if="!toggleOne">
                    <div class="u-margin-bottom-20 u-bold">
                        Employment Status
                    </div>
                    <control for="employmentType" :table-control="true" :icon-space="false">
                        <select name="employmentType"
                                v-model="period.employmentType"
                                id="employmentType"
                                v-field-control:employmentType.required="{type:'text'}"
                                @change="updatePostInjuryIncomePeriod()"
                                required>
                            <option value="" disabled selected>Please select...</option>
                            <option value="employed">Employed</option>
                            <option value="self_employed">Self-employed</option>
                            <option value="part_employed">Part-employed</option>
                            <option value="absence">Absent</option>
                        </select>
                        <control-error :show="$error('employmentType') || period.employmentType === null || period.employmentType === ''">
                            Employment status is required
                        </control-error>
                    </control>
                </div>
                <div class="flex-grid__item--third u-padding-l-0" v-if="!toggleOne && period.employmentType !== '' && period.employmentType !== 'absence'">
                    <div class="u-margin-bottom-20 u-bold">
                        New Employment Income
                    </div>
                    <control for="employmentIncome" :table-control="true" :icon-space="false" before-after="pound">
                        <input type="number"
                               v-model.number="period.employmentIncome"
                               @input="updatePostInjuryIncomePeriod()"
                               v-on:focus="period.employmentIncome = period.employmentIncome || ''"
                               v-on:blur="period.employmentIncome = period.employmentIncome || 0"
                               v-two-dp
                               required
                               v-field-control:employmentIncome.required="{type:'number'}">
                        <control-error :show="$error('employmentIncome') || period.employmentIncome < 1">
                            Employment Income must be greater than £0
                        </control-error>
                    </control>
                </div>
                <div class="flex-grid__item--third" v-if="!toggleOne && period.employmentType !== '' && period.employmentType !== 'absence'">
                    <div class="u-d-flex">
                        <div class="u-margin-bottom-20 u-bold">
                            Net/Gross
                        </div>
                        <icon-tooltip
                                v-if="disableNetGross"
                                tooltip-name="net-gross"
                                tooltip-text="ASHE Data are gross median figures. Therefore the Net function has been disabled. To re-enable edit the employment income figure."
                                side="right"
                                text-width="200"
                                icon-type="info"
                                icon-colour="m-icon--dark">
                        </icon-tooltip>
                    </div>
                    <control for="past_loss_of_earnings" :table-control="true" :icon-space="false">
                        <select name="" v-model="period.employmentNetGross" @change="updatePostInjuryIncomePeriod()" :disabled="disableNetGross">
                            <option value="net">Net</option>
                            <option value="gross">Gross</option>
                        </select>
                    </control>
                </div>
                <div class="flex-grid__item--third" v-if="!toggleOne && period.employmentType !== '' && period.employmentType !== 'absence'">
                    <div class="u-margin-bottom-20 u-bold">
                        Total Annual Net Income
                    </div>
                    <control for="past_loss_of_earnings" :table-control="true" :icon-space="false" before-after="pound">
                        <input type="text" :value="period.totalNetIncome" disabled v-two-dp>
                    </control>
                </div>

                <div class="u-margin-top-20 u-full-width" v-if="!toggleOne">
                    <button class="a-button a-button--small u-margin-right-10"
                            v-if="period.employmentType !== '' && period.employmentType !== 'absence'"
                            @click="selectSalary(null)">
                        Use ASHE Average
                    </button>
                    <button class="a-button a-button--small u-margin-right-10"
                            v-if="period.employmentType !== '' && period.employmentType !== 'absence'"
                            @click="openDialog">
                        Refine by Occupation
                    </button>
                    <button class="a-button a-button--small u-margin-right-10 u-margin-bottom-10"
                            :class="{'disabled' : postInjuryIncomePeriodPayments.length > 0}"
                            @click="addPayment()">Add Payment Received</button>
                </div>

                <div class="u-d-flex u-flex-item-full u-margin-top-30" v-if="postInjuryIncomePeriodPayments.length && !toggleOne">
                        <h2>Payments Received for Period {{ index +1}}</h2>
                        <div style="margin: 5px 0 0 10px;" @click="togglePayments()">
                            <icon-tooltip
                                    tooltip-name="pre-accident-income"
                                    tooltip-text="To toggle the payments received between visible and hidden just select this icon."
                                    side="right"
                                    text-width="200"
                                    icon-type="toggle"
                                    icon-colour="m-icon--dark">
                            </icon-tooltip>
                    </div>
                </div>

                <div class="u-full-width" v-for="(payment, paymentIndex) in postInjuryIncomePeriodPayments" v-if="!toggleOne && !togglePayment">
                    <post-accident-payments
                        :periodStart="period.startOfPeriod"
                        :periodEnd="period.endOfPeriod"
                        :payment="payment"
                        :paymentIndex="paymentIndex"
                        :periodIndex="index"
                    ></post-accident-payments>
                </div>
                <div class="u-full-width u-margin-top-10 u-margin-bottom-20" v-if="postInjuryIncomePeriodPayments.length && !toggleOne && !togglePayment">
                    <button class="a-button a-button--small" @click="addPayment()">Add Another Payment</button>
                </div>


                <div class="flex-grid__item--third" :class="{'u-padding-l-0' : !toggleOne && !togglePayment}" v-if="postInjuryIncomePeriodPayments.length && !toggleOne && !togglePayment">
                    <div class="u-margin-bottom-20 u-bold">
                        Total Payments Received
                    </div>
                    <control for="past_loss_of_earnings" :table-control="true" :icon-space="false" before-after="pound">
                        <input type="text" :value="totalPaymentsReceived" v-two-dp disabled>
                    </control>
                </div>

                <div class="flex-grid__item--third" :class="{'u-padding-l-0' : !postInjuryIncomePeriodPayments.length && !toggleOne || !toggleOne && togglePayment}">
                    <div class="u-margin-bottom-20 u-bold">
                        Total Net Loss
                    </div>
                    <control for="past_loss_of_earnings" :table-control="true" :icon-space="false" before-after="pound">
                        <input type="text" v-model="period.totalNetLoss" v-two-dp disabled>
                    </control>
                </div>
            </div>

        </div>

        <modal :name="'ons_salary_'+this.uniqueId" no-default-button="true">
            <div class="p-day-one__refine-occupation">
                <h2>Refine by occupation</h2>

                <div v-if="selectedNode">
                    <p>
                        Your current selection - <strong>{{ getLastNode().description }}</strong><br/>
                        <span v-if="getLastNode().code.length !== 3">Select a salary, expand down further or use the average salary for this whole group</span>
                        <span v-else>Select a salary or use the average salary for this whole group</span>
                    </p>

                    <button @click="unselectNode(selectedNode)" class="a-button a-button--small">Back</button>
                    <button @click="selectSalary(selectedNode)" class="a-button a-button--small">Use average {{displaySalary(selectedNode)}}</button>
                </div>
                <div v-else>
                    <p>
                        Select a salary or expand the group
                    </p>
                </div>
                <div>
                    <div>
                        <table class=" a-table__small a-table a-table--full-width">
                            <thead>
                            <tr>
                                <th>Code</th>
                                <th>Description</th>
                                <th>Salary</th>
                                <th width="30"></th>
                                <th width="30"></th>
                            </tr>
                            </thead>
                            <tbody v-if="loading">
                            <td colspan="5" style="text-align: center;"><div class="a-loader--table"></div></td>
                            </tbody>
                            <tbody v-else>
                            <tr v-for="node in nodes" class="selected--row">
                                <td>{{node.code}}</td>
                                <td>{{node.description}}</td>
                                <td>{{displaySalary(node)}}</td>
                                <td>
                                    <button :disabled="node.code.length === 4" class="a-button a-button--small" @click.stop="selectNode(node)">
                                        Expand
                                    </button>
                                </td>
                                <td>
                                    <button v-if="node.salary!=='x'" class="a-button a-button--small" @click.stop="selectSalary(node, type)">
                                        Select
                                    </button>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </modal>

    </div>
</template>

<script>
    import {mapGetters, mapActions} from 'vuex';
    import Control from 'components/form/Control';
    import ControlError from 'components/form/control/Error';
    import ControlWarning from 'components/form/control/Warning.vue';
    import DateInput from 'components/form/control/DateInput.vue';
    import IconTooltip from 'components/IconTooltip';
    import FormValidator from 'components/form/validator/mixin';
    import currencyFormat from 'mixins/currencyFormat';
    import ClaimsDashboardIcons from 'pages/claims-dashboard/components/Icons';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import Api from 'services/Api';
    import Modal from 'components/modal/Modal';
    import ModalContent from 'components/modal/ModalContent';
    import ModalInTheWild from 'components/modal/ModalInTheWild';
    import PostAccidentPayments from './PostAccidentPayments';

    export default {
        name: 'post-accident-earnings',
        props: {
            period: {
                type: Object,
                required: true
            },
            index: {
                type: Number,
                required: true
            },
            maxIndex: {
                type: Number,
                default: 0
            },
        },
        mounted() {
          if (this.index === 0) {
              this.period.startOfPeriod = this.getClaimInfo('dateOfInjury');
          }
        },
        watch: {
            ['periodLength'] () {
                this.calculatePeriodNetTotal();
            },
            ['periodTotalNetIncome'] () {
                this.calculatePeriodNetTotal();
            },
            ['periodPaymentsTotal'] () {
                this.calculatePeriodNetTotal();
            },
            ['getPreAccidentNetTotal'] () {
                this.calculatePeriodNetTotal();
            },
            ['periodTotalNetLoss'] () {
                this.updatePostInjuryIncomePeriod();
            },
            ['periodEmploymentIncome'] () {
                this.disableNetGross = false
            }
        },
        data () {
            return {
                loading: false,
                nodes: [],
                selectedNode: null,
                selectedNodes: [],
                toggleOne: false,
                togglePayment: false,
                toggleCalcOne: true,
                disableNetGross: false
            }
        },
        components: {
            Control,
            ControlError,
            ControlWarning,
            DateInput,
            IconTooltip,
            ClaimsDashboardIcons,
            Modal,
            ModalContent,
            ModalInTheWild,
            PostAccidentPayments
        },
        mixins: [
            currencyFormat,
            FormValidator
        ],
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        computed: {
            ...mapGetters('personal/claimant', [
                'claimant',
                'getClaimInfo',
                'getIncomePeriod',
                'getPreAccidentNetTotal',
                'getPostInjuryIncomePeriods',
                'getPostInjuryIncomePeriodPayments'
            ]),
            ...mapGetters('modal', ['modals']),
            periodLength() {
                return this.getPostInjuryIncomePeriods.length;
            },
            periodEmploymentIncome() {
                return this.period.employmentIncome;
            },
            periodTotalNetIncome() {
                return this.period.totalNetIncome;
            },
            periodTotalNetLoss() {
                return this.period.totalNetLoss;
            },
            periodPaymentsTotal() {
                return this.postInjuryIncomePeriodPayments
                    .reduce((total, carry) => total + carry.total ,0);
            },
            postInjuryIncomePeriodPayments() {
                return this.getPostInjuryIncomePeriodPayments({index: this.index});
            },
            uniqueId() {
                return this._uid;
            },
            isModalOpen() {
                let ons_salary = 'ons_salary_' + this.uniqueId;
                return this.modals && this.modals[ons_salary];
            },
            isValid() {
                if (this.period.employmentType !== 'absence') {
                    return (
                        this.period.startOfPeriod !== ""
                        && this.period.endOfPeriod !== ""
                        && (this.period.employmentIncome !== "" || this.period.employmentIncome !== 0)
                        && this.period.totalNetIncome > 0
                    )
                }
                return (
                    this.period.startOfPeriod !== ""
                    && this.period.endOfPeriod !== ""
                )
            },
            paymentsValid() {
                return this.postInjuryIncomePeriodPayments.reduce((valid, value) => valid ? (value.total !== 0 && value.date !== "") : false, true)
            },
            totalPaymentsReceived() {
                return this.postInjuryIncomePeriodPayments.reduce((carry, item) => carry + parseFloat(item.total), 0);
            },
            afterEndOfPeriodRule() {
                return moment(this.period.endOfPeriod, 'DD/MM/YYYY').isAfter(moment(this.getClaimInfo('dateOfAssumedSettlement'), 'DD/MM/YYYY'));
            }
        },
        methods: {
            ...mapActions('personal/claimant', [
                'addPostInjuryIncomePeriod',
                'updateIncomePeriod',
                'addPostInjuryPeriodPayment',
                'deletePostInjuryPeriod',
                'deleteIncomePeriod',
                'updateIncomePeriodValue',
                'addIncomePeriod'
            ]),
            ...mapActions('modal', ['toggle']),
            updatePostInjuryIncomePeriod() {
                if (this.period.endOfPeriod === ""
                    || this.period.endOfPeriod === null
                    || this.period.startOfPeriod === ""
                    || this.period.startOfPeriod === null
                    || this.period.employmentType === ""
                    || this.period.employmentIncome === ""
                    || this.period.employmentIncome === null) {
                    return;
                }

                if (this.period.employmentType === 'absence') {
                    this.period.employmentIncome = 0
                }

                this.updateIncomePeriod({
                    index: this.index,
                    type: 'postInjury',
                    period: this.period
                });

                this.updateNextPeriod();
            },
            updateNextPeriod() {
                let maxIndex = this.maxIndex - 1;

                if (this.index < maxIndex) {
                    let nextIndex = this.index + 1;
                    let nextDay = moment(this.period.endOfPeriod, 'DD/MM/YYYY').add(1, 'days').format('DD/MM/YYYY');

                    this.updateIncomePeriodValue({ type: 'postInjury', index: nextIndex, key: 'startOfPeriod', value: nextDay});

                    for (let i = nextIndex, len = maxIndex; i <= len; i++) {
                        let prevPeriodEndOfPeriod = '';
                        let periodEndOfPeriod = '';
                        let periodStartOfPeriod = '';

                        prevPeriodEndOfPeriod = this.getIncomePeriod({ type: 'postInjury', index: i - 1}).endOfPeriod;
                        periodEndOfPeriod = this.getIncomePeriod({ type: 'postInjury', index: i }).endOfPeriod;
                        periodStartOfPeriod = this.getIncomePeriod({ type: 'postInjury', index: i }).startOfPeriod;

                        if (prevPeriodEndOfPeriod > periodStartOfPeriod) {
                            periodStartOfPeriod = moment(prevPeriodEndOfPeriod, 'DD/MM/YYYY').format('DD/MM/YYYY');
                            this.updateIncomePeriodValue({ type: 'postInjury', index: i, key: 'startOfPeriod', value: periodStartOfPeriod})
                        }

                        if (periodEndOfPeriod <= periodStartOfPeriod && i !== len) {
                            this.updateIncomePeriodValue({ type: 'postInjury', index: i, key: 'endOfPeriod', value: periodStartOfPeriod})
                        }
                    }
                }

                if (moment(this.period.endOfPeriod, 'DD/MM/YYYY').isBefore(moment(this.getClaimInfo('dateOfAssumedSettlement'), 'DD/MM/YYYY'))
                    && moment(this.period.endOfPeriod, 'DD/MM/YYYY').isAfter(moment(this.period.startOfPeriod, 'DD/MM/YYYY'))
                    && this.index === maxIndex) {
                    this.addIncomePeriod('postInjury');
                }
            },
            addPayment() {
                this.addPostInjuryPeriodPayment({
                    index: this.index
                });
            },
            removePeriod() {
                let maxIndex = this.maxIndex - 1;
                let prevIndex = this.index - 1;
                let nextIndex = this.index + 1;
                let nextPeriodEndOfPeriod = this.getIncomePeriod({ type: 'postInjury', index: prevIndex }).endOfPeriod;
                let nextDay = moment(nextPeriodEndOfPeriod, 'DD/MM/YYYY').add(1, 'days').format('DD/MM/YYYY');

                (this.index === maxIndex) ? this.updateIncomePeriodValue({ type: 'postInjury', index: prevIndex, key: 'endOfPeriod', value: this.period.endOfPeriod})
                    : this.updateIncomePeriodValue({ type: 'postInjury', index: nextIndex, key: 'startOfPeriod', value: nextDay});

                this.deleteIncomePeriod({index: this.index, type: 'postInjury'});
            },
            toggleFirst() {
                this.toggleOne = !this.toggleOne;
            },
            togglePayments() {
                this.togglePayment = !this.togglePayment;
            },
            openDialog() {
                if (!this.isModalOpen) {
                    this.nodes = [];
                    this.selectedNode = null;
                    this.selectedNodes = [];
                    return this.toggle('ons_salary_' + this.uniqueId).then(this.updateNodes);
                }
            },
            closeModal() {
                if (this.isModalOpen) {
                    return this.toggle('ons_salary_' + this.uniqueId);
                }
            },
            updateNodes(code = null) {
                this.loading = true;
                Api.calculators.ons.salary(this.gender, code)
                    .then(nodes => {
                        this.nodes = nodes;
                        this.loading = false;
                    });
            },
            selectNode(node) {
                if (node.code.length === 4) {
                    return;
                }
                this.selectedNode = node;
                this.selectedNodes.push(node);
                return this.updateNodes(node.code);
            },
            unselectNode(node) {
                let index = this.selectedNodes.indexOf(node);
                if (index === 0) {
                    this.selectedNodes = [];
                    this.selectedNode = null;
                    return this.updateNodes();
                }
                this.selectedNodes.splice(index);
                let lastIndex = this.selectedNodes.length - 1;
                this.selectedNode = this.selectedNodes[lastIndex];
                this.updateNodes(this.selectedNode.code);
            },
            selectSalary(node) {
                if (node === null) {
                    return Api.calculators.ons.averageSalary(this.claimant.gender)
                        .then(result => {
                            this.period.employmentIncome = result.value;
                            this.period.employmentNetGross = 'gross';
                            this.updatePostInjuryIncomePeriod();
                        }).then(() => {
                            this.closeModal;
                            this.disableNetGross = true;
                        });
                }
                this.period.employmentIncome = node.salary;
                this.updatePostInjuryIncomePeriod();
                this.closeModal();
            },
            displaySalary(node) {
                if (node.salary === "x") {
                    return "No Information";
                }
                return this.currencyFormat(node.salary, '£', 0);
            },
            calculatePeriodNetTotal() {
                let duration = moment(this.period.endOfPeriod, 'DD/MM/YYYY')
                    .diff(moment(this.period.startOfPeriod, 'DD/MM/YYYY'), 'years', true);

                this.period.totalNetLoss = (duration * this.getPreAccidentNetTotal) -
                    (duration * this.period.totalNetIncome) -
                    this.periodPaymentsTotal || 0;
            },
            getLastNode() {
                return this.selectedNodes.slice(-1)[0];
            },
        }
    }

</script>


// WEBPACK FOOTER //
// PostAccidentEarnings.vue?2a93589c